import { useNav } from '../nav'
import { useCharts } from './chartsData'
import './systems.css'

const ELEMENT_COLORS: Record<string, string> = {
  wood: '#5E9C7A',
  fire: '#D26A4A',
  earth: '#C9924D',
  metal: '#C7CBD9',
  water: '#3A4A80',
}

/**
 * Короткие подписи дворцов: в клетке 87×87 «Перемены и поездки» превращаются
 * в «ПЕРЕМЕНЫ И…». Полное название остаётся в разборе, здесь нужно узнаваемое.
 */
const PALACE_SHORT: Record<string, string> = {
  life: 'Судьба',
  siblings: 'Братья',
  spouse: 'Пара',
  children: 'Дети',
  wealth: 'Деньги',
  health: 'Здоровье',
  travel: 'Дороги',
  friends: 'Друзья',
  career: 'Дело',
  property: 'Дом',
  fortune: 'Дух',
  parents: 'Родители',
}

/**
 * Дворцы в карте Цзы Вэй стоят не по порядку списка, а по земным ветвям:
 * сетка 4×4, ветви идут по кругу от 巳 в левом верхнем углу. Центр — сам человек.
 */
const BRANCH_CELLS: Record<number, { row: number; column: number }> = {
  5: { row: 1, column: 1 },
  6: { row: 1, column: 2 },
  7: { row: 1, column: 3 },
  8: { row: 1, column: 4 },
  9: { row: 2, column: 4 },
  10: { row: 3, column: 4 },
  11: { row: 4, column: 4 },
  0: { row: 4, column: 3 },
  1: { row: 4, column: 2 },
  2: { row: 4, column: 1 },
  3: { row: 3, column: 1 },
  4: { row: 2, column: 1 },
}

/** Одна комната расчётов: карта одной системы во всю страницу. */
export function Room({ system }: { system: 'bazi' | 'ziwei' | 'tzolkin' }) {
  const { data, error } = useCharts()

  if (error) return <div className="state">{error}</div>
  if (!data) return <div className="state">Открываем карты…</div>

  if (system === 'bazi') {
    return (
      <div className="screen systems">
        <RoomHead
          title="Четыре опоры"
          term="Ба Цзы · 八字"
          note="Столпы твоего рождения: из чего ты сделан и в какой среде растёшь."
        />

        <div className="pillars">
          {data.bazi.pillars.map((pillar) => (
            <div className={`pillar${pillar.available ? '' : ' pillar-empty'}`} key={pillar.id}>
              <div className="pillar-title">{pillar.title}</div>
              {pillar.available ? (
                <>
                  <div className="pillar-stem cjk" style={{ color: ELEMENT_COLORS[pillar.element ?? ''] ?? 'var(--text)' }}>
                    {pillar.stemZh}
                  </div>
                  <div className="pillar-branch cjk">{pillar.branchZh}</div>
                  <div className="pillar-note">{pillar.stemRu} {pillar.branchRu}</div>
                </>
              ) : (
                <div className="pillar-note">нужно время рождения</div>
              )}
            </div>
          ))}
        </div>

        <div className="lbl sect">Поток стихий</div>
        <div className="elements">
          {data.bazi.elements.map((element) => (
            <div className="element-row" key={element.element}>
              <span className="element-name">{element.titleRu}</span>
              <span className="element-bar">
                <span
                  className="element-fill"
                  style={{ width: `${Math.max(3, element.percent)}%`, background: ELEMENT_COLORS[element.element] }}
                />
              </span>
              <span className="element-percent">{element.percent}%</span>
            </div>
          ))}
        </div>
        {(data.bazi.overloaded.length > 0 || data.bazi.missing.length > 0) && (
          <div className="element-note">
            {data.bazi.overloaded.length > 0 && <div>С избытком: {data.bazi.overloaded.join(', ')}.</div>}
            {data.bazi.missing.length > 0 && <div>Совсем нет: {data.bazi.missing.join(', ')}.</div>}
          </div>
        )}

        {data.bazi.luck.length > 0 && (
          <>
            <div className="lbl sect">Большие периоды</div>
            <div className="luck-row">
              {data.bazi.luck.map((cycle) => (
                <div className="luck" key={`${cycle.fromYear}-${cycle.stemZh}`}>
                  <div className="luck-age">{cycle.fromAge !== null ? `с ${cycle.fromAge} лет` : '—'}</div>
                  <div className="luck-pillar cjk">
                    {cycle.stemZh}
                    {cycle.branchZh}
                  </div>
                  <div className="luck-year">{cycle.fromYear ?? ''}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    )
  }

  if (system === 'ziwei') {
    if (!data.ziwei) {
      return (
        <div className="screen systems">
          <RoomHead
            title="Город двенадцати дворцов"
            term="Цзы Вэй Доу Шу · 紫微斗數"
            note="Эта карта строится только по точному времени рождения."
          />
          <div className="element-note">Без времени рождения дворцы не расставить: ветвь часа задаёт всю сетку.</div>
        </div>
      )
    }
    const ziwei = data.ziwei
    const life = ziwei.palaces.find((palace) => palace.isLife)
    return (
      <div className="screen systems">
        <RoomHead
          title="Город двенадцати дворцов"
          term="Цзы Вэй Доу Шу · 紫微斗數"
          note="Ночная карта: в каждом дворце живёт своя звезда."
        />

        <div className="palaces">
          {ziwei.palaces.map((palace) => {
            const cell = BRANCH_CELLS[palace.branchIndex]
            if (!cell) return null
            return (
              <div
                className={`pal${palace.isLife ? ' hot' : ''}`}
                key={palace.id}
                style={{ gridRow: cell.row, gridColumn: cell.column }}
              >
                <div className="nm">{PALACE_SHORT[palace.id] ?? palace.titleRu}</div>
                {palace.isBody && <div className="mark" />}
                {palace.stars.length > 0 && (
                  <div className="star">
                    <div className="zh">{palace.stars[0]?.nameZh}</div>
                    <div className="ru">{palace.stars[0]?.nameRu}</div>
                  </div>
                )}
              </div>
            )
          })}
          <div className="center">
            <div className="zh">命盤</div>
            <div className="who">Ты</div>
            <div className="dec">{ziwei.bureauRu}</div>
            {life && (
              <div className="note">
                {life.stars.length > 0
                  ? `в Судьбе — ${life.stars.map((star) => star.nameRu).join(', ')}`
                  : 'дворец Судьбы без главных звёзд'}
              </div>
            )}
          </div>
        </div>

        {ziwei.transformations.length > 0 && (
          <>
            <div className="lbl sect">Четыре превращения года</div>
            <div className="rowlist">
              {ziwei.transformations.map((item) => (
                <div className="rowitem" key={item.titleZh}>
                  <div>
                    <div className="ttl">
                      {item.titleRu} · <span className="cjk">{item.titleZh}</span>
                    </div>
                    <div className="note">
                      {item.starRu} <span className="cjk">{item.starZh}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    )
  }

  const { tzolkin } = data
  return (
    <div className="screen systems">
      <RoomHead
        title="Печать времени"
        term="Цолькин · Dreamspell"
        note="Современная система галактической подписи, связанная с календарём Цолькин — не сам календарь майя."
      />

      <div className="kin-card">
        <div className="kin-number">Кин {tzolkin.kin}</div>
        <div className="kin-seal">{tzolkin.sealRu}</div>
        <div className="kin-tone">
          тон {tzolkin.toneIndex} · {tzolkin.toneRu}
        </div>
      </div>

      <div className="lbl sect">Волна</div>
      <div className="wave">
        {Array.from({ length: 13 }, (_, index) => index + 1).map((position) => (
          <span key={position} className={`wave-dot${position === tzolkin.wave.position ? ' on' : ''}`}>
            {position}
          </span>
        ))}
      </div>
      <div className="element-note">
        Волна начинается с кина {tzolkin.wave.startKin}. Сегодня ты на {tzolkin.wave.position}-м дне из тринадцати.
      </div>
    </div>
  )
}

/**
 * Шапка комнаты. Кнопка «назад» остаётся дублем системной стрелки Telegram:
 * в браузере (стенд) стрелки нет вовсе, а внутри Telegram её замечают не все.
 */
function RoomHead({ title, term, note }: { title: string; term: string; note: string }) {
  const { back } = useNav()
  return (
    <>
      <div className="topbar">
        <div className="brand">АТЛАС</div>
        <div className="topnote">расчёты</div>
      </div>
      <button type="button" className="back" onClick={back}>
        <svg width="14" height="14" viewBox="0 0 14 14">
          <path d="M9 2 4 7l5 5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        ВСЕ РАСЧЁТЫ
      </button>
      <h1 className="display">{title}</h1>
      <div className="sub">
        <span className="room-term">{term}</span> — {note}
      </div>
    </>
  )
}
